import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Search } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { SEO } from "@/components/SEO";
import { ErrorState, statusFromError } from "@/components/ErrorState";
import { Pagination } from "@/components/Pagination";
import { SafeHtml } from "@/components/SafeHtml";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export function Blog() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["blog", page, q], queryFn: async () => (await api.get("/blog", { params: { page, limit: 9, q } })).data, placeholderData: (p) => p });
  return (
    <div className="container-x py-10 sm:py-14" data-testid="blog-page">
      <SEO title="Blog" description="News, guides and updates from Fileuploady." />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Blog</h1><p className="mt-2 text-slate-500">Guides, updates and tips on sharing files.</p></div>
        <div className="relative sm:w-64"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input placeholder="Search posts…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-10 rounded-xl pl-9" data-testid="blog-search" /></div>
      </div>
      {isLoading ? <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div> : data?.items.length === 0 ? (
        <p className="mt-16 text-center text-slate-500" data-testid="blog-empty">No posts published yet.</p>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md" data-testid={`blog-card-${p.slug}`}>
              {p.featured_image ? <img src={p.featured_image} alt="" className="aspect-[16/9] w-full object-cover" loading="lazy" /> : <div className="aspect-[16/9] w-full bg-gradient-to-br from-indigo-50 to-slate-100" />}
              <div className="p-5">
                <p className="flex items-center gap-1.5 text-xs text-slate-400"><Calendar className="h-3.5 w-3.5" />{formatDate(p.published_at)}</p>
                <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-900 group-hover:text-indigo-600">{p.title}</h2>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-500">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
      {data && <Pagination page={page} limit={9} total={data.total} onChange={setPage} />}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading, error } = useQuery({ queryKey: ["blog-post", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data, retry: false });
  if (isLoading) return <div className="container-x max-w-3xl py-12"><Skeleton className="h-10 w-3/4" /><Skeleton className="mt-6 h-64 w-full" /></div>;
  if (error) return <ErrorState status={statusFromError(error)} title="Post not found" text="This article does not exist or is not published." action={<Link to="/blog" className="text-indigo-600 hover:underline">Back to blog</Link>} />;
  const jsonLd = { "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.seo_description || post.excerpt, datePublished: post.published_at, dateModified: post.updated_at, author: { "@type": "Person", name: post.author_name }, image: post.featured_image || undefined };
  return (
    <article className="container-x max-w-3xl py-10 sm:py-14" data-testid="blog-post-page">
      <SEO title={post.seo_title || post.title} description={post.seo_description || post.excerpt} image={post.featured_image} type="article" jsonLd={jsonLd} />
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600" data-testid="blog-back-link"><ArrowLeft className="h-4 w-4" />All posts</Link>
      <header className="mt-6">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="blog-post-title">{post.title}</h1>
        <p className="mt-3 text-sm text-slate-500">{post.author_name} · {formatDate(post.published_at)}</p>
        {post.tags?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{post.tags.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">{t}</span>)}</div>}
      </header>
      {post.featured_image && <img src={post.featured_image} alt="" className="mt-8 w-full rounded-2xl border border-slate-200" />}
      <SafeHtml className="prose-clean mt-8" html={post.content_html} testId="blog-post-content" />
    </article>
  );
}

export function LegalPage({ slug }) {
  const { data, isLoading } = useQuery({ queryKey: ["legal", slug], queryFn: async () => (await api.get(`/legal/${slug}`)).data });
  const pages = [["terms", "Terms"], ["privacy", "Privacy"], ["cookies", "Cookies"], ["abuse", "Abuse & DMCA"]];
  return (
    <div className="container-x py-10 sm:py-14" data-testid={`legal-page-${slug}`}>
      <SEO title={data?.title || "Legal"} />
      <div className="grid gap-10 lg:grid-cols-[200px_1fr]">
        <nav className="flex gap-2 overflow-x-auto lg:sticky lg:top-24 lg:flex-col lg:self-start" aria-label="Legal pages">
          {pages.map(([s, label]) => <Link key={s} to={`/${s}`} className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium ${s === slug ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`legal-nav-${s}`}>{label}</Link>)}
        </nav>
        <div className="max-w-3xl">
          {isLoading ? <Skeleton className="h-64 w-full" /> : <SafeHtml className="prose-clean" html={data?.content_html} testId="legal-content" />}
        </div>
      </div>
    </div>
  );
}

export function NotFound() {
  return <ErrorState status={404} title="Page not found" text="The page you are looking for does not exist." />;
}
